/**
 * FollowingTabContent Component
 * Displays paginated list of users the current user is following.
 * Mirrors FollowersTabContent but uses the getFollowing endpoint.
 */

import { useQuery } from "@tanstack/react-query"
import { Loader2, User } from "lucide-react"
import { useRef, useState } from "react"
import { Button } from "@/components/ui/button"
import { useAuthStore } from "@/features/auth/store/authStore"
import { FollowerCard } from "@/features/profile/components/FollowerCard"
import { getFollowing } from "@/features/profile/services/follow.service"
import { useDebounce } from "@/hooks/useDebounce"
import { useTranslation } from "@/language"

interface FollowingTabContentProps {
  userId: string
  searchQuery?: string
}

export function FollowingTabContent({ userId, searchQuery }: FollowingTabContentProps) {
  const { t } = useTranslation("myRituality")
  const [page, setPage] = useState(1)
  const currentUser = useAuthStore(state => state.user)
  const debouncedSearch = useDebounce(searchQuery || "", 500)

  const prevDebouncedSearch = useRef(debouncedSearch)
  if (prevDebouncedSearch.current !== debouncedSearch) {
    prevDebouncedSearch.current = debouncedSearch
    setPage(1)
  }

  const { data, isLoading, isError } = useQuery({
    queryKey: ["following", userId, page, debouncedSearch],
    queryFn: () => getFollowing(userId, page, undefined, debouncedSearch),
    enabled: !!userId,
  })

  const followers = data?.following || []
  const pages = data?.pages ?? 1

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    )
  }

  if (isError) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-sm text-red">{t("followersSubTabs.error")}</p>
      </div>
    )
  }

  if (followers.length === 0) {
    return (
      <div className="text-center py-12">
        <User className="mx-auto mb-2 h-12 w-12 text-primary/30" />
        <p className="text-sm text-primary/50">{t("followersSubTabs.emptyFollowing")}</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 lg:grid-cols-3 md:grid-cols-2 md:gap-5 md:my-6">
        {followers.map(item => (
          <FollowerCard key={item.id} item={item} currentUserId={currentUser?.id} />
        ))}
      </div>

      {pages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(p => p - 1)}
            disabled={page === 1}
          >
            {t("followersSubTabs.previous")}
          </Button>
          <span className="text-sm text-primary/50 px-2">
            {page} / {pages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(p => p + 1)}
            disabled={page === pages}
          >
            {t("followersSubTabs.next")}
          </Button>
        </div>
      )}
    </div>
  )
}
